import type { Msg91WidgetConfig } from "../config";

/**
 * MSG91's OTP widget, driven from OUR form rather than its own popup.
 *
 * With `exposeMethods: true`, `initSendOTP` installs three globals the page
 * calls directly -- measured working on 2026-09-13 (send, resend by SMS,
 * resend by voice call, verify):
 *
 *   window.sendOtp("91XXXXXXXXXX", ok, fail)        ok.message = request id
 *   window.retryOtp("11" | "4", ok, fail, reqId)   11 = SMS, 4 = voice call
 *   window.verifyOtp(code, ok, fail, reqId)         ok.message = access token
 *
 * 🚨 THE ACCESS TOKEN PROVES NOTHING IN THE BROWSER. Anything here can be
 * forged by the person at the keyboard. It is sent to radaccess, which asks
 * MSG91 server-side whether it is genuine AND proves this exact mobile, and
 * accepts each token once (radaccess handler/loginwidget.go).
 *
 * ⚠️ The widget throttles per IP address (code 408 "IPBlocked", about three
 * requests a minute). Fine for an operator on their own connection.
 */

type Ok = (data: unknown) => void;
type Fail = (error: unknown) => void;

interface Msg91Globals {
  initSendOTP?: (configuration: Record<string, unknown>) => void;
  sendOtp?: (identifier: string, ok: Ok, fail: Fail) => void;
  retryOtp?: (channel: string, ok: Ok, fail: Fail, reqId?: string) => void;
  verifyOtp?: (otp: string, ok: Ok, fail: Fail, reqId?: string) => void;
  getWidgetData?: () => unknown;
}

const SCRIPT_URLS = ["https://verify.msg91.com/otp-provider.js", "https://verify.phone91.com/otp-provider.js"];

/** A refusal from the widget, with MSG91's own code and words. */
export class WidgetError extends Error {
  readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = "WidgetError";
    this.code = code;
  }
  /** 408 IPBlocked: not a wrong code -- try again shortly. */
  get throttled(): boolean {
    return this.code === "408" || /ipblocked/i.test(this.message);
  }
}

export interface Msg91Widget {
  /** Send a code to an E.164 Indian mobile (+91…). Resolves to the request id. */
  send(mobileE164: string): Promise<string>;
  resend(channel: "sms" | "voice", reqId: string): Promise<void>;
  /** Check the code. Resolves to the access token radaccess must confirm. */
  verify(code: string, reqId: string): Promise<string>;
}

const globals = () => window as unknown as Msg91Globals;

function asWidgetError(error: unknown): WidgetError {
  const e = (error ?? {}) as { code?: unknown; message?: unknown };
  const code = e.code === undefined || e.code === null ? "" : String(e.code);
  const message = typeof e.message === "string" && e.message ? e.message : "MSG91 refused the request";
  return new WidgetError(message, code);
}

function messageOf(data: unknown): string {
  const m = (data ?? {}) as { message?: unknown };
  return typeof m.message === "string" ? m.message : "";
}

function loadScript(urls: string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    let i = 0;
    const attempt = () => {
      const s = document.createElement("script");
      s.src = urls[i]!;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        s.remove();
        i++;
        if (i < urls.length) attempt();
        else reject(new WidgetError("The MSG91 widget could not be loaded", "load_failed"));
      };
      document.head.appendChild(s);
    };
    attempt();
  });
}

/**
 * True once the widget can send correctly: its methods are installed AND its
 * settings have loaded. A script without getWidgetData (older builds) is
 * judged on sendOtp alone, as before.
 */
function widgetReady(): boolean {
  const g = globals();
  if (typeof g.sendOtp !== "function") return false;
  if (typeof g.getWidgetData !== "function") return true;
  try {
    return Boolean(g.getWidgetData());
  } catch {
    return false;
  }
}

let loading: Promise<Msg91Widget> | null = null;

/**
 * Load and initialise the widget once per page. A failed load is forgotten,
 * so pressing Send again retries it.
 */
export function loadMsg91Widget(cfg: Msg91WidgetConfig, timeoutMs = 15_000): Promise<Msg91Widget> {
  if (loading) return loading;
  loading = (async () => {
    if (typeof globals().initSendOTP !== "function") await loadScript(SCRIPT_URLS);
    const init = globals().initSendOTP;
    if (typeof init !== "function") throw new WidgetError("The MSG91 widget did not start", "init_missing");
    init({
      widgetId: cfg.widgetId,
      tokenAuth: cfg.tokenAuth,
      exposeMethods: true,
      // Required by the widget; our calls pass their own callbacks.
      success: () => undefined,
      failure: () => undefined,
    });
    // 🚨 sendOtp EXISTING IS NOT THE WIDGET BEING READY. The widget installs
    // sendOtp/getWidgetData on window as soon as it starts, and only THEN
    // fetches its own settings (widget/getWidgetProcess). sendOtp checks those
    // settings to decide whether a captcha token must go with the request; if
    // they have not arrived it sends WITHOUT one, and MSG91 answers "Invalid
    // Captcha Token". Measured 2026-09-15: every send from a freshly loaded
    // test.ankhos.io page failed this way while the widget has captcha on.
    // So ready = the settings are loaded, not merely the method installed.
    const deadline = Date.now() + timeoutMs;
    while (!widgetReady()) {
      if (Date.now() > deadline) throw new WidgetError("The MSG91 widget did not become ready", "not_ready");
      await new Promise((r) => setTimeout(r, 100));
    }
    return widgetApi();
  })();
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

function widgetApi(): Msg91Widget {
  return {
    send: (mobileE164) =>
      new Promise((resolve, reject) => {
        // MSG91 takes the number without the plus.
        globals().sendOtp!(mobileE164.replace(/^\+/, ""), (d) => resolve(messageOf(d)), (e) => reject(asWidgetError(e)));
      }),
    resend: (channel, reqId) =>
      new Promise((resolve, reject) => {
        const retry = globals().retryOtp;
        if (typeof retry !== "function") {
          reject(new WidgetError("Resend is not available", "no_retry"));
          return;
        }
        retry(channel === "voice" ? "4" : "11", () => resolve(), (e) => reject(asWidgetError(e)), reqId);
      }),
    verify: (code, reqId) =>
      new Promise((resolve, reject) => {
        globals().verifyOtp!(
          code,
          (d) => {
            const token = messageOf(d);
            if (token) resolve(token);
            else reject(new WidgetError("MSG91 verified the code but returned no token", "no_token"));
          },
          (e) => reject(asWidgetError(e)),
          reqId,
        );
      }),
  };
}
